import { JSX, useState, useEffect, useMemo } from 'react';
import { CalendarEvent, CalendarId, calendarService } from '../services/calendarService';
import EventView from '../add-page/events/eventView';
import Loading from '../common/Loading/loading';

const Events = ({ startDate, endDate, selectedCalendar, setEndDate }: { startDate: Date, endDate: Date, selectedCalendar: CalendarId[], setEndDate: (date: Date) => void }): JSX.Element => {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isTodayTask, setIsTodayTask] = useState<boolean>(false);

    const fetchEvents = async (showLoading: boolean = false) => {
        if (showLoading) {
            setIsLoading(true);
        }
        const events = await calendarService.getEvents(startDate.toISOString(), endDate.toISOString());
        setEvents(events);
        if (showLoading) {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        // Initial fetch with loading
        fetchEvents(true);
        
        // Set up polling without loading
        const intervalId = setInterval(() => fetchEvents(false), 3 * 60 * 1000);
        
        return () => clearInterval(intervalId);
    }, [startDate, endDate]);

    const processedEvents = useMemo(() => {
        return filteredEvents.map((event, index) => {
            const datestring = event.start.date || event.start.dateTime;
            const previousDateString = filteredEvents[index - 1]?.start.date || filteredEvents[index - 1]?.start.dateTime;
            const date = new Date(datestring);
            const previousDate = previousDateString ? new Date(previousDateString) : null;
            
            // Check if current and previous dates are in different months
            const isLastDay = index > 0 && date.getMonth() !== new Date(previousDateString).getMonth();
            if (isLastDay) {
                console.log(`event: ${event.summary} is on ${date.toLocaleDateString()} and previous event is on ${new Date(previousDateString).toLocaleDateString()}`);
            }

            const displayDate = index === 0 || date.getMonth() !== new Date(previousDateString).getMonth() || date.getDate() !== new Date(previousDateString).getDate();

            return {
                event,
                isLastDay,
                displayDate,
                index
            };
        });
    }, [filteredEvents]);

    useEffect(() => {
        setFilteredEvents(events.filter(event => selectedCalendar.includes(event.calendarId)));
    }, [selectedCalendar, events]);

    useEffect(() => {
        setIsTodayTask(false);
        filteredEvents.forEach(event => {
            const date = new Date(event.start.date || event.start.dateTime);
            if (date.getMonth() === new Date().getMonth() && date.getDate() === new Date().getDate()) {
                setIsTodayTask(true);
            }
        });
    }, [filteredEvents]);

    const getDeviderClassName = (isLastDay: boolean, event: CalendarEvent) => {
        const date = new Date(event.start.date || event.start.dateTime);
        if (date.getDay() === 1)
            return 'events-divider-horizontal';
        if (isLastDay) {
            return isLastDay ? 'divider-horizontal-lastday' : 'event-divider-dashes';
        }
        return 'event-divider-dashes';
    }

    const handleLoadMore = () => {
        setEndDate(new Date(endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()));
    }

    return (
        <div style={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ 
                flex: 1, 
                overflowY: 'auto',
                padding: '8px'
            }}>
                {!isTodayTask && (
                    <>
                        <EventView event={null} displayDate={true} handelEventUpdate={()=> {}}/>
                    </>
                )}
                {processedEvents.map(({ event, isLastDay, displayDate, index }) => (
                    <div key={event.id}>
                        {displayDate && !(index === 0 && isTodayTask) && (
                            <div className={getDeviderClassName(isLastDay, event)}/>
                        )}
                        <EventView event={event} displayDate={displayDate} handelEventUpdate={()=> {}}/>
                        {index === processedEvents.length - 1 && (
                            <div className='load-more-container'>
                                <p className='load-more-text' onClick={() => handleLoadMore()}>Load more ...</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {isLoading && <Loading />}
        </div>
    )   
}

export default Events;
